import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getVideoBySlug, getRelatedVideos } from '@/mocks/videos';
import { getCategoryBySlug } from '@/mocks/categories';
import VideoCard from '@/components/base/VideoCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const video = getVideoBySlug(slug || '');
  const relatedVideos = video ? getRelatedVideos(video.slug, video.category) : [];
  const [activeTab, setActiveTab] = useState<'intro' | 'cast'>('intro');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!video) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-950 mb-2">视频未找到</h1>
          <p className="text-foreground-500 mb-6">该视频不存在或已被移除</p>
          <Link to="/" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = getCategoryBySlug(video.category);
  const formatViews = (views: number) => {
    if (views >= 10000) return (views / 10000).toFixed(1) + '万次播放';
    return views + '次播放';
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: video.title,
    description: video.description,
    image: video.thumbnail,
    datePublished: video.publishDate,
    author: {
      '@type': 'Organization',
      name: '超碰在线',
    },
    publisher: {
      '@type': 'Organization',
      name: '超碰在线',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <main className="pt-20 pb-14">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: category?.name || video.category, to: `/category/${video.category}` },
                { label: video.title },
              ]}
            />
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <article className="flex-1 min-w-0">
              {/* Video Player Area */}
              <div className="relative aspect-video rounded-2xl overflow-hidden bg-foreground-950 mb-6">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  title={video.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <button className="w-20 h-20 flex items-center justify-center rounded-full bg-primary-500/90 hover:bg-primary-500 hover:scale-110 transition-all cursor-pointer group">
                    <i className="ri-play-fill text-background-50 text-4xl ml-1 group-hover:ml-1.5 transition-all"></i>
                  </button>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                  <span className="text-white/90 text-sm font-medium">{video.duration}</span>
                  <div className="flex items-center gap-3">
                    <button className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white cursor-pointer transition-colors">
                      <i className="ri-volume-up-line"></i>
                    </button>
                    <button className="w-9 h-9 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white cursor-pointer transition-colors">
                      <i className="ri-fullscreen-line"></i>
                    </button>
                  </div>
                </div>
              </div>

              {/* Title & Meta */}
              <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-3">
                {video.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatViews(video.views)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  {video.rating}
                </span>
                <span>{video.publishDate}</span>
                {video.region && <span>{video.region}</span>}
                {video.year && <span>{video.year}</span>}
                <span className="flex items-center gap-1 text-primary-500">
                  <i className="ri-thumb-up-line"></i> 9.8万
                </span>
                <span className="flex items-center gap-1 text-foreground-400">
                  <i className="ri-thumb-down-line"></i> 236
                </span>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Tabs */}
              <div className="flex items-center gap-1 mb-5 px-1 py-1 bg-background-100 rounded-full w-fit">
                <button
                  onClick={() => setActiveTab('intro')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === 'intro'
                      ? 'bg-background-50 text-foreground-950 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  视频介绍
                </button>
                <button
                  onClick={() => setActiveTab('cast')}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === 'cast'
                      ? 'bg-background-50 text-foreground-950 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  演职员表
                </button>
              </div>

              {/* Tab Content */}
              <div className="bg-background-50 border border-background-200 rounded-xl p-5">
                {activeTab === 'intro' ? (
                  <div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{video.description}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {video.director && (
                      <div className="flex items-start gap-3">
                        <span className="text-sm font-medium text-foreground-500 w-14 flex-shrink-0 whitespace-nowrap">导演</span>
                        <span className="text-sm text-foreground-900">{video.director}</span>
                      </div>
                    )}
                    {video.actors && video.actors.length > 0 && (
                      <div className="flex items-start gap-3">
                        <span className="text-sm font-medium text-foreground-500 w-14 flex-shrink-0 whitespace-nowrap">主演</span>
                        <span className="text-sm text-foreground-900">{video.actors.join(' / ')}</span>
                      </div>
                    )}
                    {video.region && (
                      <div className="flex items-start gap-3">
                        <span className="text-sm font-medium text-foreground-500 w-14 flex-shrink-0 whitespace-nowrap">地区</span>
                        <span className="text-sm text-foreground-900">{video.region}</span>
                      </div>
                    )}
                    {video.year && (
                      <div className="flex items-start gap-3">
                        <span className="text-sm font-medium text-foreground-500 w-14 flex-shrink-0 whitespace-nowrap">年份</span>
                        <span className="text-sm text-foreground-900">{video.year}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Share */}
              <div className="flex items-center gap-3 mt-6 pt-5 border-t border-background-200">
                <span className="text-sm text-foreground-500">分享：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 hover:text-primary-600 text-foreground-600 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 hover:text-primary-600 text-foreground-600 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 hover:text-primary-600 text-foreground-600 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
            </article>

            {/* Sidebar - Related Videos */}
            <aside className="lg:w-[340px] flex-shrink-0">
              <div className="sticky top-24">
                <h3 className="text-lg font-bold text-foreground-950 font-heading mb-4">相关推荐</h3>
                <div className="space-y-3" data-product-shop="">
                  {relatedVideos.length > 0 ? (
                    relatedVideos.map((v) => (
                      <Link
                        key={v.id}
                        to={`/article/${v.slug}`}
                        className="flex gap-3 p-2 rounded-xl hover:bg-background-100 transition-colors cursor-pointer group"
                      >
                        <div className="w-[140px] flex-shrink-0 aspect-video rounded-lg overflow-hidden relative">
                          <img
                            src={v.thumbnail}
                            alt={v.title}
                            title={v.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          <span className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 text-background-50 text-[10px] rounded">
                            {v.duration}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0 py-1">
                          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug mb-1">
                            {v.title}
                          </h4>
                          <div className="flex items-center gap-2 text-xs text-foreground-500">
                            <span className="flex items-center gap-0.5">
                              <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                              {v.rating}
                            </span>
                            <span>{v.year}</span>
                          </div>
                        </div>
                      </Link>
                    ))
                  ) : (
                    <p className="text-sm text-foreground-500">暂无相关推荐</p>
                  )}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}